import { Component } from '@theme/component';

const BASE_CURRENCY = 'THB';
const STORAGE_KEY = 'jbs-display-currency';
// Kept in step with the <option> list in snippets/currency-selector.liquid.
// Rates for all of these ship in Shopify's currencies.js.
const SUPPORTED_CURRENCIES = new Set([
  'THB',
  'USD',
  'TWD',
  'JPY',
  'SGD',
  'CNY',
  'HKD',
  'AUD',
  'EUR',
  'KRW',
  'CAD',
  'GBP',
]);

/** Countries whose own currency this selector offers. */
const COUNTRY_CURRENCY = {
  TH: 'THB',
  US: 'USD',
  TW: 'TWD',
  JP: 'JPY',
  SG: 'SGD',
  CN: 'CNY',
  HK: 'HKD',
  AU: 'AUD',
  KR: 'KRW',
  CA: 'CAD',
  GB: 'GBP',
};

/**
 * Europe, broadly: anywhere here that is not in COUNTRY_CURRENCY falls back to
 * EUR, which a European visitor can size up even where it is not their own
 * money — Poland uses PLN, Norway NOK, and neither is worth carrying for the
 * handful of visitors involved. Everywhere else falls back to USD.
 *
 * TR and RU are included; both are transcontinental and the choice between EUR
 * and USD for them is a coin toss that barely matters for a display currency.
 */
const EUROPE = new Set([
  'AD', 'AL', 'AT', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE',
  'ES', 'FI', 'FO', 'FR', 'GG', 'GI', 'GR', 'HR', 'HU', 'IE', 'IM', 'IS', 'IT',
  'JE', 'LI', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL',
  'PT', 'RO', 'RS', 'RU', 'SE', 'SI', 'SK', 'SM', 'TR', 'UA', 'VA', 'XK',
]);

/** Shopify's own IP geolocation. Same origin, no configuration, no third party. */
const GEO_ENDPOINT = '/browsing_context_suggestions.json';

/** How long to hold the first conversion waiting for geolocation. */
const GEO_TIMEOUT_MS = 1500;

/**
 * @typedef {Object} MoneyState
 * @property {string} baseText - The original THB text rendered by Shopify
 * @property {string} renderedText - The last text written by this converter
 */

/**
 * @typedef {Object} CurrencySelectorRefs
 * @property {HTMLSelectElement} select - Display-currency select
 */

class CurrencyConverter {
  /** @type {Set<CurrencySelector>} */
  #selectors = new Set();

  /** @type {WeakMap<HTMLElement, MoneyState>} */
  #moneyStates = new WeakMap();

  /** @type {Map<string, Intl.NumberFormat>} */
  #formatters = new Map();

  /** @type {MutationObserver | null} */
  #observer = null;

  #currency = BASE_CURRENCY;
  #ready = false;
  #initialized = false;
  #hasExplicitChoice = false;
  /** @type {Promise<string | null> | null} */
  #detection = null;

  initialize() {
    if (this.#initialized) return;
    this.#initialized = true;

    const stored = this.#readStoredCurrency();
    this.#hasExplicitChoice = stored !== null;
    this.#currency = stored ?? BASE_CURRENCY;

    // Only guess for visitors who have not chosen. Detection is deliberately
    // not persisted, so it re-runs every visit and follows someone who travels;
    // only an explicit pick is remembered.
    if (!this.#hasExplicitChoice) {
      this.#detection = this.#detectCountry();
    }

    if (this.#getRates()) {
      this.#start();
      return;
    }

    window.addEventListener('load', this.#start, { once: true });
  }

  /**
   * @param {CurrencySelector} selector
   */
  register(selector) {
    this.#selectors.add(selector);
    selector.setCurrency(this.#currency);
    selector.setReady(this.#ready);
  }

  /**
   * @param {CurrencySelector} selector
   */
  unregister(selector) {
    this.#selectors.delete(selector);
  }

  /**
   * @param {string} currency
   */
  selectCurrency(currency) {
    if (!this.#ready) return;
    // Reached only from the select's change handler, so this is the visitor
    // choosing — the one case that is remembered.
    this.#hasExplicitChoice = true;
    this.#applyCurrency(currency, true);
  }

  /**
   * @param {string} currency
   * @param {boolean} persist Whether to remember it. Detection does not.
   */
  #applyCurrency(currency, persist) {
    if (!SUPPORTED_CURRENCIES.has(currency)) return;

    this.#currency = currency;
    if (persist) this.#storeCurrency(currency);
    this.#syncSelectors();
    this.#convertWithin(document);
    this.#updateNotes();

    document.dispatchEvent(
      new CustomEvent('currency:change', {
        detail: { currency },
      })
    );
  }

  #start = async () => {
    if (this.#ready) return;

    const rates = this.#getRates();
    if (!rates) {
      console.warn('Display currency conversion is unavailable because Shopify currency rates did not load.');
      return;
    }

    const missingCurrency = [...SUPPORTED_CURRENCIES].find((currency) => typeof rates[currency] !== 'number');
    if (missingCurrency) {
      console.warn(`Display currency conversion is unavailable because the ${missingCurrency} rate is missing.`);
      return;
    }

    this.#ready = true;

    // Settle on a currency before the first conversion where possible, so the
    // visitor does not watch prices change under them. Capped, because a slow
    // geolocation lookup should not hold the page in the base currency.
    if (this.#detection) {
      const country = await Promise.race([
        this.#detection,
        new Promise((resolve) => setTimeout(() => resolve(null), GEO_TIMEOUT_MS)),
      ]);
      if (country && !this.#hasExplicitChoice) {
        this.#currency = this.#currencyForCountry(country);
      }
      // If the lookup was merely slow rather than broken, apply it when it
      // lands — unless the visitor has since chosen for themselves.
      this.#detection
        .then((late) => {
          if (late && !this.#hasExplicitChoice && this.#ready) {
            this.#applyCurrency(this.#currencyForCountry(late), false);
          }
        })
        .catch(() => {});
    }

    this.#syncSelectors();
    this.#convertWithin(document);
    this.#updateNotes();

    if (!document.body) return;

    this.#observer = new MutationObserver(this.#handleMutations);
    this.#observer.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true,
      // Section hydration morphs the header — cart drawer included — onto the
      // existing nodes, putting the notice's `hidden` back as the server sent
      // it. No node is added, so only an attribute mutation reveals it.
      attributes: true,
      attributeFilter: ['hidden'],
    });
  };

  #syncSelectors() {
    for (const selector of this.#selectors) {
      selector.setCurrency(this.#currency);
      selector.setReady(this.#ready);
    }
  }

  /**
   * Shows the cart's approximate-price notice, but only while a currency other
   * than the one the order is actually charged in is on display. Rounding
   * widens the gap between the figure shown and the amount taken, so the
   * shopper is told before they reach checkout rather than after.
   */
  #updateNotes() {
    const show = this.#ready && this.#currency !== BASE_CURRENCY;

    // Writes only on a difference: these writes are themselves observed, so an
    // unconditional one would re-trigger this method.
    for (const note of document.querySelectorAll('[data-currency-note]')) {
      if (note.hasAttribute('hidden') === show) note.toggleAttribute('hidden', !show);
      const code = note.querySelector('[data-currency-note-code]');
      if (code && code.textContent !== this.#currency) code.textContent = this.#currency;
    }
  }

  /**
   * @param {Node} root
   */
  #convertWithin(root) {
    if (root instanceof Element && root.matches('.money')) {
      this.#convertMoneyElement(/** @type {HTMLElement} */ (root));
    }

    if (!(root instanceof Document || root instanceof DocumentFragment || root instanceof Element)) return;

    for (const element of root.querySelectorAll('.money')) {
      this.#convertMoneyElement(/** @type {HTMLElement} */ (element));
    }
  }

  /**
   * @param {MutationRecord[]} mutations
   */
  #handleMutations = (mutations) => {
    /** @type {Set<Element>} */
    const moneyElements = new Set();

    for (const mutation of mutations) {
      if (mutation.target instanceof Element) {
        const closestMoney = mutation.target.closest('.money');
        if (closestMoney) moneyElements.add(closestMoney);
      } else if (mutation.target.parentElement) {
        const closestMoney = mutation.target.parentElement.closest('.money');
        if (closestMoney) moneyElements.add(closestMoney);
      }

      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches('.money')) moneyElements.add(node);
        for (const element of node.querySelectorAll('.money')) {
          moneyElements.add(element);
        }
      }
    }

    for (const element of moneyElements) {
      this.#convertMoneyElement(/** @type {HTMLElement} */ (element));
    }

    // The notice comes back in its server-rendered state two ways: the cart
    // summary is re-rendered when the cart changes, which adds a fresh element,
    // and section hydration morphs the header after load, which reuses the
    // element but resets its `hidden` and its currency code. Catch both.
    const noteTouched = mutations.some((mutation) => {
      const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      if (target?.closest('[data-currency-note]')) return true;
      return [...mutation.addedNodes].some(
        (node) =>
          node instanceof Element &&
          (node.matches('[data-currency-note]') || node.querySelector('[data-currency-note]'))
      );
    });
    if (noteTouched) this.#updateNotes();
  };

  /**
   * @param {HTMLElement} element
   */
  #convertMoneyElement(element) {
    const currentText = element.textContent ?? '';
    const previousState = this.#moneyStates.get(element);
    const baseText = previousState && currentText === previousState.renderedText ? previousState.baseText : currentText;
    const renderedText = this.#currency === BASE_CURRENCY ? baseText : this.#convertText(baseText, this.#currency);

    this.#moneyStates.set(element, { baseText, renderedText });

    if (currentText !== renderedText) {
      element.textContent = renderedText;
    }
  }

  /**
   * @param {string} baseText
   * @param {string} currency
   * @returns {string}
   */
  #convertText(baseText, currency) {
    const amount = this.#parseBaseAmount(baseText);
    const rates = this.#getRates();
    if (amount === null || !rates) return baseText;

    const baseRate = rates[BASE_CURRENCY];
    const targetRate = rates[currency];
    if (typeof baseRate !== 'number' || typeof targetRate !== 'number') return baseText;

    const convertedAmount = (amount * baseRate) / targetRate;
    return this.#getFormatter(currency).format(this.#roundUp(convertedAmount));
  }

  /**
   * Rounds *up* to a step scaled to the size of the number.
   *
   * Up rather than to-nearest on purpose: this is a display-only conversion and
   * the order is charged in THB, so rounding down would show a figure lower
   * than what is actually taken. Rounding up is never understated.
   *
   * The step scales because the supported currencies span four orders of
   * magnitude — dropping cents tidies USD but would leave KRW reading as an
   * arbitrary 61,910.
   *
   *   44.67 -> 45      563.09 -> 570      7,015 -> 7,020      61,910 -> 62,000
   *
   * @param {number} amount
   * @returns {number}
   */
  #roundUp(amount) {
    const step = amount < 100 ? 1 : amount < 10_000 ? 10 : 100;
    return Math.ceil(amount / step) * step;
  }

  /**
   * @param {string} value
   * @returns {number | null}
   */
  #parseBaseAmount(value) {
    const match = value.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
    if (!match) return null;

    const amount = Number(match[0]);
    return Number.isFinite(amount) ? amount : null;
  }

  /**
   * @param {string} currency
   * @returns {Intl.NumberFormat}
   */
  #getFormatter(currency) {
    const cachedFormatter = this.#formatters.get(currency);
    if (cachedFormatter) return cachedFormatter;

    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      // Amounts are rounded up to a whole step before formatting, so the
      // fraction digits Intl would add by default are always .00 noise.
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    this.#formatters.set(currency, formatter);
    return formatter;
  }

  /**
   * @returns {Record<string, number> | null}
   */
  #getRates() {
    return window.Currency?.rates ?? null;
  }

  /**
   * @returns {string}
   */
  #readStoredCurrency() {
    try {
      const currency = localStorage.getItem(STORAGE_KEY);
      return currency && SUPPORTED_CURRENCIES.has(currency) ? currency : null;
    } catch {
      return null;
    }
  }

  /**
   * Maps a detected country to a currency this selector offers.
   * @param {string} country ISO 3166-1 alpha-2
   * @returns {string}
   */
  #currencyForCountry(country) {
    return COUNTRY_CURRENCY[country] ?? (EUROPE.has(country) ? 'EUR' : 'USD');
  }

  /**
   * Asks Shopify which country the visitor is browsing from.
   * @returns {Promise<string | null>}
   */
  async #detectCountry() {
    try {
      const response = await fetch(GEO_ENDPOINT, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const data = await response.json();
      return data?.detected_values?.country?.handle ?? null;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} currency
   */
  #storeCurrency(currency) {
    try {
      localStorage.setItem(STORAGE_KEY, currency);
    } catch {
      // Currency selection still works for the current page when storage is unavailable.
    }
  }
}

const currencyConverter = new CurrencyConverter();

/**
 * Header and mobile-drawer control for selecting a display currency.
 *
 * @extends {Component<CurrencySelectorRefs>}
 */
class CurrencySelector extends Component {
  requiredRefs = ['select'];

  /** @type {MutationObserver | null} */
  #selectObserver = null;

  connectedCallback() {
    super.connectedCallback();
    currencyConverter.register(this);

    /*
     * The header section is re-hydrated after load (section-hydration.js), which
     * replaces this component's children with fresh server markup. That markup
     * ships the select `disabled` with THB selected, because it has to be inert
     * until rates load.
     *
     * The swap is not routed through updatedCallback: the section is the morph
     * target, not this element, and this element itself is never replaced — so
     * neither connectedCallback nor updatedCallback fires and nothing re-applies
     * the current state. The mobile drawer is re-hydrated this way and the header
     * selector is not, which is why the drawer came back permanently disabled
     * while the header worked.
     *
     * Attributes are watched as well as children, because the usual case is not
     * a swapped node at all: morph reconciles the existing <select> against the
     * fresh server markup and copies `disabled` straight back onto it. That is
     * an attribute mutation with no childList change, no disconnect and no
     * updatedCallback, which is why watching children alone did not catch it.
     *
     * This cannot loop. Re-registering writes the correct value, and assigning a
     * property its current value produces no further mutation record.
     */
    this.#selectObserver = new MutationObserver(() => currencyConverter.register(this));
    this.#selectObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#selectObserver?.disconnect();
    this.#selectObserver = null;
    currencyConverter.unregister(this);
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    super.updatedCallback();
    currencyConverter.register(this);
  }

  /**
   * The live select. `this.refs` can point at a detached node after the
   * re-hydration described above, so it is only trusted while still ours.
   * @returns {HTMLSelectElement | null}
   */
  get #select() {
    const fromRefs = this.refs.select;
    if (fromRefs?.isConnected && this.contains(fromRefs)) return fromRefs;
    return this.querySelector('select');
  }

  /**
   * @param {Event} event
   */
  changeCurrency(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    currencyConverter.selectCurrency(event.target.value);
  }

  /**
   * @param {string} currency
   */
  setCurrency(currency) {
    const select = this.#select;
    if (select) select.value = currency;
  }

  /**
   * @param {boolean} ready
   */
  setReady(ready) {
    const select = this.#select;
    if (select) select.disabled = !ready;
  }
}

if (!customElements.get('currency-selector')) {
  customElements.define('currency-selector', CurrencySelector);
}

currencyConverter.initialize();
